import type { Job } from '@/api/endpoints/jobs'

/**
 * A descrição do toast de job concluído: quantos vídeos o job produziu, ou
 * enviou, lida de onde cada tipo de job a grava.
 *
 * - Job das factories: os vídeos prontos vêm em `detail.videos`.
 * - Ciclo do scheduler (`sched-gen-*`, `sched-upl-*`): o `detail` não traz
 *   vídeo nenhum, e a contagem está em `result.completed`, `result.failed` e,
 *   no upload, `result.skipped` (`_run_channel` e `_finalize_upload_job` no
 *   backend). Lido só do `detail`, todo ciclo saía como "Terminou sem produzir
 *   vídeos", inclusive o que gerou vídeo (13/10/2026). A geração do Scheduler
 *   na fila (`scheduled_generation`, F3) devolve o mesmo `result`.
 * - Capas dos Shorts (`studio_thumbnail`): o `detail.videos` lista as capas
 *   processadas, inclusive as recusadas, e cinco capas de canal sem
 *   verificação viravam "5 vídeos prontos". A drenagem já resume o resultado
 *   na mensagem do job ("2 aplicada(s) · 0 falha(s) · …"), e é ela que vai.
 *
 * Sem nada para contar, devolve `undefined`: um upload avulso não produz
 * vídeo, e o toast não deve dizer que ele terminou sem produzir.
 */
export function successDescription(job: Job): string | undefined {
  if (job.type === 'studio_thumbnail') return job.message.trim() || undefined
  if (job.type === 'channel_recovery.apply') return recoverySummary(job.result)
  const skipped = skippedReason(job.result)
  if (skipped) return `Não gerou: ${skipped}.`
  const cycle = cycleCounts(job.result)
  if (cycle) return job.category === 'upload' ? uploadSummary(cycle) : generationSummary(cycle)
  const videos = job.detail?.['videos']
  if (Array.isArray(videos))
    return generationSummary({ completed: videos.length, failed: 0, skipped: 0 })
  return undefined
}

interface CycleCounts {
  completed: number
  failed: number
  skipped: number
}

/**
 * A geração do Scheduler na fila (`scheduled_generation`, F3) termina
 * `succeeded` com `status: "skipped"` quando não gera: o slot venceu na fila
 * ou o canal deixou de gerar. O motivo vem em `result.skipped` e vai no lugar
 * do "Terminou sem produzir vídeos". (No upload, `skipped` é uma contagem.)
 */
function skippedReason(result: Job['result']): string | null {
  if (result?.['status'] !== 'skipped') return null
  const reason = result['skipped']
  return typeof reason === 'string' && reason ? reason : null
}

function cycleCounts(result: Job['result']): CycleCounts | null {
  const completed = result?.['completed']
  if (typeof completed !== 'number') return null
  return { completed, failed: countOf(result?.['failed']), skipped: countOf(result?.['skipped']) }
}

function countOf(value: unknown): number {
  return typeof value === 'number' ? value : 0
}

function plural(n: number, one: string, many: string): string {
  return n === 1 ? `1 ${one}.` : `${String(n)} ${many}.`
}

function generationSummary({ completed, failed }: CycleCounts): string {
  const parts = [
    completed === 0
      ? 'Terminou sem produzir vídeos.'
      : plural(completed, 'vídeo pronto', 'vídeos prontos'),
  ]
  if (failed > 0) parts.push(plural(failed, 'falhou', 'falharam'))
  return parts.join(' ')
}

function uploadSummary({ completed, failed, skipped }: CycleCounts): string {
  const parts = [
    completed === 0
      ? 'Nenhum vídeo enviado.'
      : plural(completed, 'vídeo enviado', 'vídeos enviados'),
  ]
  if (failed > 0) parts.push(plural(failed, 'falhou', 'falharam'))
  if (skipped > 0) parts.push(plural(skipped, 'pulado', 'pulados'))
  return parts.join(' ')
}

const RECOVERY_STATE: Record<string, string> = {
  done: 'lote concluído',
  undone: 'lote desfeito',
  waiting: 'lote aguardando (cota ou YouTube)',
  failed: 'lote parado com erro',
  paused: 'lote pausado',
}

/**
 * Uma passada do Recuperar canal (`channel_recovery.apply`): o estado em que o
 * lote ficou e as contagens da passada, de `result.status` e `result.counts`.
 */
function recoverySummary(result: Job['result']): string | undefined {
  const state = typeof result?.['status'] === 'string' ? result['status'] : ''
  if (!state) return undefined
  const counts = result?.['counts']
  const parts: string[] = []
  if (counts && typeof counts === 'object') {
    const c = counts as Record<string, unknown>
    const done = countOf(c['done']) + countOf(c['undone'])
    if (done > 0) parts.push(plural(done, 'vídeo trocado', 'vídeos trocados').replace(/\.$/, ''))
    const failed = countOf(c['failed'])
    if (failed > 0) parts.push(plural(failed, 'falhou', 'falharam').replace(/\.$/, ''))
  }
  return [RECOVERY_STATE[state] ?? state, ...parts].join(' · ')
}
